import { zodResolver } from "@hookform/resolvers/zod"
import { isPossiblePhoneNumber } from "react-phone-number-input"
import React, { useState } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { gql } from "~/__generated__"
import { useViewerMaybe } from "~/auth/use-viewer"
import { useNavigate } from "react-router-dom"
import { onboardingAccountDetailsPath } from "~/common/paths"
import { useFormErrors } from "~/common/use-form-errors"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { TextField } from "~/fields/text-field"
import { Button } from "~/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/ui/form"
import { PhoneInput } from "~/ui/phone-input"
import { useToast } from "~/ui/use-toast"

const phoneFormSchema = z.object({
  phone: z
    .string()
    .min(1, { message: "Phone number is required" })
    .refine(isPossiblePhoneNumber, { message: "Invalid phone number" }),
})

const verificationFormSchema = z.object({
  token: z.string().min(1, { message: "Verification code is required" }),
})

type PhoneFormValues = z.infer<typeof phoneFormSchema>
type VerificationFormValues = z.infer<typeof verificationFormSchema>

const USER_PHONE_CREATE_MUTATION = gql(/* GraphQL */ `
  mutation UserPhoneCreate($input: UserPhoneCreateInput!) {
    userPhoneCreate(input: $input) {
      userPhone {
        id
        phone
        phoneFormatted
        status
      }
    }
  }
`)

const USER_PHONE_VERIFY_MUTATION = gql(/* GraphQL */ `
  mutation UserPhoneVerify($input: UserPhoneVerifyInput!) {
    userPhoneVerify(input: $input) {
      userPhone {
        id
        phone
        phoneFormatted
        status
        user {
          id
          userStatus
        }
      }
    }
  }
`)

export const OnboardingVerifyPhoneScreen: React.FC = () => {
  const [phoneSubmitted, setPhoneSubmitted] = useState(false)
  const [userPhoneId, setUserPhoneId] = useState<string>("")
  const [userPhoneCreate, userPhoneCreateResult] = useSafeMutation(USER_PHONE_CREATE_MUTATION)
  const [userPhoneVerify, userPhoneVerifyResult] = useSafeMutation(USER_PHONE_VERIFY_MUTATION)
  const { toast } = useToast()
  const navigate = useNavigate()
  const {
    result: { refetch: refetchViewer },
  } = useViewerMaybe()

  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneFormSchema),
    defaultValues: {
      phone: "",
    },
  })

  const verificationForm = useForm<VerificationFormValues>({
    resolver: zodResolver(verificationFormSchema),
    defaultValues: {
      token: "",
    },
  })

  useFormErrors(phoneForm.setError, userPhoneCreateResult)
  useFormErrors(verificationForm.setError, userPhoneVerifyResult)

  const onPhoneSubmit = async (values: PhoneFormValues) => {
    const result = await userPhoneCreate({
      variables: {
        input: {
          input: {
            phone: values.phone,
          },
        },
      },
    })

    if (result.errors) {
      return
    }

    setUserPhoneId(result.data?.userPhoneCreate.userPhone.id || "")
    setPhoneSubmitted(true)
    toast({
      title: "Verification SMS Sent",
      description: "Please check your phone for the verification code.",
      variant: "default",
    })
  }

  const onVerificationSubmit = async (values: VerificationFormValues) => {
    const result = await userPhoneVerify({
      variables: {
        input: {
          userPhoneId,
          code: values.token,
        },
      },
    })

    if (result.errors) {
      return
    }

    await refetchViewer()
    toast({
      title: "Phone Verified",
      description: "Your phone has been successfully verified.",
      variant: "default",
    })

    // Navigate to account details
    navigate(onboardingAccountDetailsPath({}))
  }

  const handleSendAgain = () => {
    setPhoneSubmitted(false)
    phoneForm.reset()
    verificationForm.reset()
  }

  if (phoneSubmitted) {
    return (
      <div>
        <div className="space-y-2">
          <h1 className="text-xs text-[#777]">Verification</h1>
          <h2 className="text-lg font-medium text-[#333]">Enter verification code</h2>
        </div>
        <hr className="mb-10 mt-6 border-t border-[#EAEAEA]" style={{ height: "0.5px" }} />
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            A verification code was sent to the phone number provided. Please check your messages
            and input the code provided.
          </p>
          <Form {...verificationForm}>
            <form
              onSubmit={verificationForm.handleSubmit(onVerificationSubmit)}
              className="space-y-6"
            >
              <TextField
                control={verificationForm.control}
                name="token"
                label="Code Input"
                key="userPhoneVerifyCode"
                placeholder="000000"
                className="text-center tracking-widest"
                autoComplete="one-time-code"
                inputMode="numeric"
              />
              <Button type="submit" disabled={userPhoneVerifyResult.loading}>
                {userPhoneVerifyResult.loading ? "Verifying..." : "Verify Code"}
              </Button>
            </form>
          </Form>
          <p className="text-sm text-gray-500">
            Didn't receive the code?{" "}
            <button
              type="button"
              onClick={handleSendAgain}
              className="text-gray-500 underline hover:text-gray-700"
            >
              Send Again
            </button>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="space-y-2">
        <h1 className="text-xs text-[#777]">Verification</h1>
        <h2 className="text-lg font-medium text-[#333]">Next, let's verify your phone number</h2>
      </div>
      <hr className="mb-10 mt-6 border-t border-[#EAEAEA]" style={{ height: "0.5px" }} />
      <div className="space-y-6">
        <p className="text-sm text-[#777777]">
          Please verify your phone number. Verifying your phone number provides an additional layer
          of security.
        </p>
        <Form {...phoneForm}>
          <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-6">
            <FormField
              control={phoneForm.control}
              name="phone"
              render={({ field }) => (
                <FormItem className="flex flex-col items-start">
                  <FormLabel className="text-left">Phone Number</FormLabel>
                  <FormControl className="w-full">
                    <PhoneInput defaultCountry="US" {...field} />
                  </FormControl>
                  <FormDescription className="text-left">Enter a phone number</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={userPhoneCreateResult.loading}>
              {userPhoneCreateResult.loading ? "Sending..." : "Send Code"}
            </Button>
          </form>
        </Form>
        <p className="text-xs text-[#777777]">
          By clicking send you agree to the terms and conditions and we will send you a verification
          code via SMS message to the number provided. Standard text and messaging rates may apply.
        </p>
      </div>
    </div>
  )
}
